import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Plus, X } from 'lucide-react';
import { bookingApi } from '../api/bookingApi';
import { servicesApi } from '../api/servicesApi';

const inputClass = 'box-border w-full rounded-2xl border-0 bg-slate-100/80 px-4 py-3.5 text-[15px] text-slate-950 outline-none ring-1 ring-inset ring-slate-200 transition-all duration-200 placeholder:text-slate-400 hover:bg-white hover:ring-slate-300 focus:bg-white focus:ring-2 focus:ring-green-500';

const AdditionalTasksModal = ({ booking, tasks = [], canAddTasks, giggerName, clientRate = 0, onClose, onChanged }) => {
  const [service, setService] = useState(null);
  const [taskName, setTaskName] = useState('');
  const [details, setDetails] = useState('');
  const [loadingService, setLoadingService] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const hasPendingTask = tasks.some((task) => task.status === 'pending');
  const checklist = Array.isArray(service?.checklist) ? service.checklist : [];
  const selectedHours = useMemo(() => {
    const raw = service?.taskDurations?.[taskName];
    const hours = Number(raw);
    return Number.isFinite(hours) && hours >= 0.5 ? hours : (taskName ? 0.5 : 0);
  }, [service, taskName]);

  useEffect(() => {
    let cancelled = false;
    setLoadingService(true);
    servicesApi.list()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const match = list.find((item) => String(item._id) === String(booking?.service?._id));
        setService(match || null);
      })
      .catch(() => {
        if (!cancelled) setService(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingService(false);
      });
    return () => { cancelled = true; };
  }, [booking?.service?._id]);

  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = async () => {
    if (!taskName || hasPendingTask || busy) return;
    setBusy(true);
    setError('');
    try {
      await bookingApi.requestExtraHours(booking.trackingId, { taskName, details: details.trim() });
      setTaskName('');
      setDetails('');
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send the task request.');
    } finally {
      setBusy(false);
    }
  };

  const TaskRow = ({ task }) => {
    const badge = task.status === 'accepted' ? 'Added' : task.status === 'declined' ? 'Declined' : `Awaiting ${giggerName}`;
    const badgeClass = task.status === 'accepted' ? 'bg-green-50 text-green-700' : task.status === 'declined' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
    const cost = (Number(task.hours) || 0) * clientRate;
    return (
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0">
            <div className="break-words text-sm font-bold text-slate-950">{task.taskName || task.description}</div>
            {task.details && <div className="mt-1 break-words text-xs leading-5 text-slate-500">{task.details}</div>}
          </div>
          <span className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${badgeClass}`}>{badge}</span>
        </div>
        <div className="mt-2 flex min-w-0 flex-col gap-2 text-xs text-slate-500 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
          <span className="min-w-0 break-words">
            {task.status === 'accepted'
              ? `+${task.hours} hr added to the job`
              : task.status === 'declined'
                ? 'Not added — book it separately if still needed'
                : `Expected time: ${task.hours || 0.5} hr`}
          </span>
          {task.status === 'accepted' && <span className="shrink-0 font-black text-green-700">+${cost.toFixed(2)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-4" onClick={onClose}>
      <div className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex min-w-0 items-center gap-2">
            <ClipboardList size={19} className="shrink-0 text-green-700" />
            <h2 className="m-0 min-w-0 break-words text-lg font-black text-slate-950">Additional tasks</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600"><X size={19} /></button>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <p className="m-0 break-words text-xs leading-5 text-slate-500">Choose from the service task list. Giggs sets the expected task time so you and {giggerName} know what is being requested before it is accepted.</p>
          {tasks.length === 0 ? (
            <div className="mt-4 rounded-2xl bg-slate-50 p-5 text-center text-sm text-slate-500">No additional tasks yet.{canAddTasks ? ' Request one below.' : ''}</div>
          ) : (
            <div className="mt-4 space-y-3">{tasks.map((task) => <TaskRow task={task} key={task._id} />)}</div>
          )}
        </div>

        {canAddTasks && (
          <div className="shrink-0 border-t border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
            {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</div>}
            {loadingService ? (
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Loading available tasks…</div>
            ) : checklist.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">No task list has been configured for this service yet. Contact Giggs support if you need to add work outside the original booking.</div>
            ) : (
              <>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold text-slate-700">Choose an additional task</span>
                  <select className={`${inputClass} cursor-pointer appearance-auto bg-white`} value={taskName} onChange={(event) => setTaskName(event.target.value)} disabled={hasPendingTask}>
                    <option value="">Select a task…</option>
                    {checklist.map((task) => <option key={task} value={task}>{task}</option>)}
                  </select>
                </label>
                {taskName && <div className="mt-2 rounded-xl bg-green-50 px-3.5 py-2.5 text-xs font-semibold text-green-800">Expected task time: {selectedHours} hour{selectedHours === 1 ? '' : 's'}</div>}
                <label className="mt-3 block">
                  <span className="mb-2 block text-xs font-bold text-slate-700">Extra details <span className="font-medium text-slate-400">(optional)</span></span>
                  <textarea className={`${inputClass} min-h-20 resize-y`} maxLength={500} value={details} onChange={(event) => setDetails(event.target.value)} rows={2} placeholder="Add the exact item, room, priority or outcome to avoid back-and-forth." disabled={hasPendingTask} />
                </label>
                <button disabled={busy || !taskName || hasPendingTask} onClick={send} className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-0 bg-green-700 px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">{busy ? 'Sending...' : hasPendingTask ? 'A task is already pending' : <><Plus size={16} /> Send request</>}</button>
              </>
            )}
            {hasPendingTask && <p className="mb-0 mt-2 break-words text-xs leading-5 text-slate-500">You can send another task once {giggerName} responds to the pending one.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalTasksModal;
